// TenantMenuPanel.jsx — 테넌트 관리 › 테넌트 탭 › 테넌트 줄 누르면 '메뉴' (2026-10-20)
//   사용자: "테넌트에서 각 테넌트를 누르고 부여할 수 있게 수정해.(회사에 메뉴 배정하는것처럼) … 메뉴명 그대로를 보여주고 부여할 수 있게".
//   예전 '모듈(메뉴)' 탭(TenantBudgetPanel)을 없애고 여기로 옮겼다(문은 하나). 모양은 회사 메뉴(CompanyMenuPanel)와 같다.
//   - 메뉴 이름 = saas_modules.name = 왼쪽 메뉴 이름(2026-10-20 시드 이름 맞춤, 흐름·매입·예약 나눔).
//   - 직원 기능 스위치(메뉴는 주되 직원 본인 기능만 빼기)는 메뉴 줄 아래 작게(사용자 결정).
//   - 아직 고객 테넌트용으로 바뀌지 않은 메뉴(grantable=false)는 켜진 경우에만 보이고 끄기만 된다(운영사 테넌트는 서버가 허용).
//   주소: GET/PUT /api/admin/saas/tenants/:code/tenant-modules · PUT …/feature-toggles (운영자 전용).
import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
// 하드코딩 메뉴·손님용 전환 전 메뉴 = 연한 붉은색 + 한 줄 설명(2026-10-27, 운영자 전용 화면)
import { hardcodedNote, NOT_CONVERTED_NOTE, RED_CARD, RED_NOTE, redConfirmPrefix } from './hardcodedMenus';

function toggleStyle(on, disabled) {
  return {
    border: `1px solid ${on ? '#16a34a' : 'var(--border)'}`,
    background: on ? '#16a34a' : 'var(--bg-subtle)',
    color: on ? '#fff' : 'var(--text-sub)',
    borderRadius: 7,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 800,
    minWidth: 64,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled && !on ? 0.6 : 1,
    whiteSpace: 'nowrap',
  };
}

function chipStyle(on, disabled) {
  return {
    border: `1px solid ${on ? '#0e7490' : 'var(--border)'}`,
    background: on ? 'rgba(14,116,144,0.12)' : 'var(--bg-card)',
    color: on ? '#0e7490' : 'var(--text-muted)',
    textDecoration: on ? 'none' : 'line-through',
    borderRadius: 999,
    padding: '1px 7px',
    fontSize: 10.5,
    fontWeight: 750,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
  };
}

const rowStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  padding: '7px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--bg-card)',
  minWidth: 0,
};

export default function TenantMenuPanel({ tenantCode, tenantName, isPlatform = false, showToast, onChanged }) {
  const [budget, setBudget] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    if (!tenantCode) return;
    setError('');
    const res = await apiClient.get(`/api/admin/saas/tenants/${tenantCode}/tenant-modules`);
    if (!res.ok) { setBudget({ modules: [] }); setError(res.data?.message || '테넌트 메뉴를 불러오지 못했습니다.'); return; }
    setBudget(res.data || { modules: [] });
  }, [tenantCode]);
  useEffect(() => { load(); }, [load]);

  const menus = budget?.modules || [];
  const onCount = menus.filter((m) => m.is_enabled).length;
  const who = tenantName || tenantCode;

  async function toggle(mod) {
    if (saving) return;
    const next = !mod.is_enabled;
    const msg = next
      ? `${redConfirmPrefix(mod.code)}${who} 에 '${mod.name}' 메뉴를 줄까요?\n\n회사마다 쓰게 하려면 회사 탭에서 회사 메뉴도 확인하세요.`
      : `${who} 에서 '${mod.name}' 메뉴를 뺄까요?\n\n이 테넌트에서 이 메뉴가 사라집니다. 기록은 지워지지 않습니다.`;
    if (!window.confirm(msg)) return;
    setSaving(mod.code);
    const res = await apiClient.put(`/api/admin/saas/tenants/${tenantCode}/tenant-modules`, { moduleCode: mod.code, isEnabled: next });
    setSaving('');
    if (!res.ok) { showToast?.(res.data?.message || '저장에 실패했습니다.', 'error'); return; }
    showToast?.(`'${mod.name}' 메뉴를 ${next ? '제공' : '미제공'}으로 저장했습니다.`, 'success');
    await load();
    if (onChanged) await onChanged();
  }

  async function toggleFeature(mod, f) {
    if (saving || !mod.is_enabled) return;
    const next = !f.enabled;
    setSaving(`${mod.code}:${f.code}`);
    const res = await apiClient.put(`/api/admin/saas/tenants/${tenantCode}/feature-toggles`, { featureCode: f.code, isEnabled: next });
    setSaving('');
    if (!res.ok) { showToast?.(res.data?.message || '저장에 실패했습니다.', 'error'); return; }
    showToast?.(`직원 기능 '${f.label}'을(를) ${next ? '켰' : '껐'}습니다.`, 'success');
    await load();
  }

  return (
    <div data-tenant-menu={tenantCode}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 800 }}>메뉴</span>
        {budget && !error && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{onCount} / {menus.length} 제공 중</span>}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.5 }}>
        이 테넌트가 쓸 수 있는 메뉴를 정합니다. 회사마다 줄 메뉴는 <strong>회사</strong> 탭에서 이 안에서 정합니다.
        {isPlatform && <> 운영사 테넌트는 회사별 메뉴(급여·매입 등 회사 열쇠)에만 바로 적용됩니다.</>}
      </div>
      {budget === null && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>불러오는 중…</div>}
      {error && <div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div>}
      {menus.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6 }}>
          {menus.map((m) => {
            const lockOn = m.grantable === false && !m.is_enabled && !isPlatform;
            const features = m.includes?.features || [];
            const hcNote = hardcodedNote(m.code);
            const notConverted = m.grantable === false && !isPlatform;
            const red = Boolean(hcNote) || notConverted;
            return (
              <div key={m.code} style={red ? { ...rowStyle, ...RED_CARD } : rowStyle} data-menu-code={m.code} data-hardcoded={hcNote ? 'true' : undefined}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                    {hcNote && <div style={RED_NOTE}>{hcNote}</div>}
                    {notConverted && <div style={RED_NOTE}>{NOT_CONVERTED_NOTE}</div>}
                  </div>
                  <button
                    type="button"
                    style={toggleStyle(m.is_enabled, saving === m.code || lockOn)}
                    disabled={saving === m.code || lockOn}
                    onClick={() => toggle(m)}
                    aria-pressed={m.is_enabled}
                  >
                    {saving === m.code ? '저장 중' : m.is_enabled ? '제공 중' : '미제공'}
                  </button>
                </div>
                {features.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 10.5, color: '#0e7490', fontWeight: 700 }}>직원 기능</span>
                    {features.map((f) => (
                      <button
                        key={`${m.code}:${f.code}`}
                        type="button"
                        data-feature-code={f.code}
                        disabled={!m.is_enabled || saving === `${m.code}:${f.code}`}
                        title={m.is_enabled ? (f.enabled ? '눌러서 이 기능만 빼기' : '눌러서 이 기능 주기') : '메뉴를 먼저 켜 주세요'}
                        onClick={() => toggleFeature(m, f)}
                        style={chipStyle(f.enabled, !m.is_enabled)}
                      >
                        {f.enabled ? '✓ ' : '✕ '}{f.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
